import { AuthenticationService } from '@app/authentication/services/authentication.service';
import { Injectable, Injector } from '@angular/core';
import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { from, Observable } from 'rxjs';
import { Log } from 'ng2-logger';
import { Router } from '@angular/router';
import { ToastsManager } from "@app/shared/util/toastr.service";
import { TranslateService } from "@ngx-translate/core";
import { environment } from "../../../environments/environment";
import { catchError, finalize, map, shareReplay, switchMap } from "rxjs/operators";
import { throwError } from "rxjs/internal/observable/throwError";
import { LEGACY_AUTH_REQUIRED_KEY } from '@models/consts/storage-constants.model';
import { MsalService } from '@azure/msal-angular';

/**
 * Interceptor for handling errors.
 */

@Injectable()
export class ErrorHandlingInterceptor implements HttpInterceptor {
    private _logger = Log.create(ErrorHandlingInterceptor.name);
    readonly X_CORRELATION_ID = "X-Correlation-ID";
    readonly listOfSkippedToastEndpoints = ['/api/toolRequests', '/api/toolReturn', '/api/issue', '/api/tickets'];
    readonly listOfSkippedErrorHandlingEndpoints = ['/api/authenticate/oauth-bosch-token'];
    readonly MSAL_RETRY_HEADER = 'X-Msal-Silent-Retry';
    private authService: AuthenticationService;
    private translateService: TranslateService;
    private router: Router;
    private toastr: ToastsManager;
    private msalService: MsalService;
    private ongoingSilentAuth$: Observable<void> | null = null;

    constructor(private injector: Injector) {
        // what the fuck angular
        setTimeout(() => {
            this.authService = this.injector.get(AuthenticationService);
            this.translateService = this.injector.get(TranslateService);
            this.router = this.injector.get(Router);
            this.toastr = this.injector.get(ToastsManager);
            this.msalService = this.injector.get(MsalService, null);
        });
    }

    intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        // Bypass intended to disable server side communciation and actions
        if (environment.mocks.offlineMode) {
            return next.handle(req);
        }


        return next.handle(req).pipe(catchError((res: HttpErrorResponse) => {
            // Skip error handling for endpoints that need custom handling (e.g., MSAL token exchange)
            for (const endpoint of this.listOfSkippedErrorHandlingEndpoints) {
                if (res.url && res.url.includes(endpoint)) {
                    return throwError(res);
                }
            }

            if (req.method === "POST" && res.status !== 401) {
                for (const endpoint of this.listOfSkippedToastEndpoints) {
                    if (res.url && res.url.includes(endpoint)) {
                        return throwError(res);
                    }
                }
            }
            switch (res.status) {
                case 400:
                case 403:
                case 409:
                case 500:
                    if (res.error && res.error.message) {
                        const errorMessage = this.translateService.instant(res.error.message);
                        this._logger.error(errorMessage);
                        this.toastr.error(errorMessage);
                    } else if (res.error instanceof Blob) {
                        res.error.text().then((error: any) => {
                            const errorMessage = error ? this.translateService.instant(JSON.parse(error)?.message) : null;
                            if (errorMessage) {
                                this._logger.error(errorMessage);
                                this.toastr.error(errorMessage);
                            }
                        });
                    }
                    const correlationId = req.headers.get(this.X_CORRELATION_ID);
                    if (correlationId) {
                        this._logger.error('Please contact the administrator and use this ID: ' + correlationId);
                        const errorMessage =
                            this.translateService.instant("errorMessage.correlationId", {correlationId: correlationId});
                        this.toastr.error(errorMessage);
                    }
                    break;
                case 401:
                    if (this.isBoschIdentifierAuthenticationRequest(req)) {
                        return throwError(res);
                    }

                    return this.attemptSilentReauth(req, next, res);
                case 404:
                    this.router.navigate(['/error404']).then();
                    break;
                default:
                    if (res && res.status) {
                        return throwError(res);
                    }
            }

            return throwError(res);
        }));
    }

    private isBoschIdentifierAuthenticationRequest(req: HttpRequest<any>): boolean {
        return req.url?.includes('/api/authenticate/boschidentifier');
    }

    /**
     * Attempts a silent MSAL token refresh + JWT exchange, then retries the failed request once.
     * Falls back to the standard 401 handling (redirect to /auth) if MSAL is disabled,
     * no account is available, or the silent refresh itself fails.
     */
    private attemptSilentReauth(req: HttpRequest<any>, next: HttpHandler, originalError: HttpErrorResponse): Observable<HttpEvent<any>> {
        if (!environment.msalEnable || !this.msalService || req.headers.has(this.MSAL_RETRY_HEADER)) {
            return this.handleUnauthorized(originalError);
        }

        return this.silentMsalReauth().pipe(
            switchMap(() => {
                const retriedReq = req.clone({
                    setHeaders: {
                        Authorization: 'Bearer ' + localStorage.getItem('user_token'),
                        [this.MSAL_RETRY_HEADER]: 'true'
                    }
                });
                return next.handle(retriedReq);
            }),
            catchError(() => this.handleUnauthorized(originalError))
        );
    }

    /**
     * Coalesces concurrent silent-refresh attempts into a single in-flight observable,
     * so multiple parallel 401s don't trigger multiple MSAL/exchange calls.
     */
    private silentMsalReauth(): Observable<void> {
        if (!this.ongoingSilentAuth$) {
            const accounts = this.msalService.instance.getAllAccounts();
            const activeAccount = this.msalService.instance.getActiveAccount() || accounts[0];

            if (!activeAccount) {
                return throwError('no_active_msal_account');
            }

            const scopes = environment.msalModuleConfig.scopes ? [environment.msalModuleConfig.scopes] : [];

            this.ongoingSilentAuth$ = from(this.msalService.instance.acquireTokenSilent({account: activeAccount, scopes})).pipe(
                switchMap(result => this.authService.exchangeMsalTokenForJwt(result.accessToken)),
                map(() => void 0),
                shareReplay(1),
                finalize(() => this.ongoingSilentAuth$ = null)
            );
        }

        return this.ongoingSilentAuth$;
    }

    private handleUnauthorized(res: HttpErrorResponse): Observable<never> {
        this._logger.error('Unauthorized ', res);
        if (res && res.error) {
            this.toastr.error(this.translateService.instant(res.error.message));
        }
        // Preserve legacy auth flag before clearing storage
        const legacyAuthRequired = sessionStorage.getItem(LEGACY_AUTH_REQUIRED_KEY);
        this.router.navigate(['/auth']).then(() => {
            localStorage.clear();
            sessionStorage.clear();
            // Restore legacy auth flag if it was set
            if (legacyAuthRequired === 'true') {
                sessionStorage.setItem(LEGACY_AUTH_REQUIRED_KEY, 'true');
            }
        });
        return throwError(res);
    }
}
